import { ref } from 'vue'
import type {
  PatientProfileData,
  PatientProfileOpenPayload,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/PatientProfile/types'
import getPatientData from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/PatientProfile/api/getPatientData'

const EMPTY_PATIENT_DATA: PatientProfileData = {
  id: null,
  title: '',
  isLoaded: false,
}

/**
 * Состояние модалки профиля автора отзыва.
 *
 * Запрос к API идёт один раз на id:
 * повторное открытие того же автора берёт кэш инстанса, без второго round-trip.
 * Ключ кэша различает `patientProfileId` и `patientId`,
 * чтобы id из разных пространств не пересекались.
 *
 * Пока ответ не пришёл, в модалке только заголовок.
 * При ошибке кэш сбрасывается в `null`, чтобы следующая попытка снова сходила в API.
 */
export default function usePatientProfileModal() {
  const isVisible = ref(false)
  const patientData = ref<PatientProfileData>({ ...EMPTY_PATIENT_DATA })
  const cachedPatientData: Record<string, PatientProfileData | null> = {}
  let currentCachedKey: string | null = null

  function close() {
    isVisible.value = false
  }

  function resetPatientData() {
    patientData.value = { ...EMPTY_PATIENT_DATA }
  }

  async function open({ patientTitle, patientProfileId, patientId }: PatientProfileOpenPayload) {
    isVisible.value = true
    resetPatientData()

    const patientDataId = Number(patientProfileId || patientId) || null

    if (!patientDataId) {
      return
    }

    const prefixForCached = patientProfileId ? 'profile' : 'default'
    const cachedKey = `${prefixForCached}-${patientDataId}`

    currentCachedKey = cachedKey

    const cached = cachedPatientData[cachedKey]

    if (cached) {
      patientData.value = cached
      return
    }

    patientData.value = {
      ...EMPTY_PATIENT_DATA,
      title: patientTitle,
    }

    try {
      cachedPatientData[cachedKey] = {
        id: patientDataId,
        title: patientTitle,
        isLoaded: false,
      }

      const ajaxPatientData = await getPatientData({ patientProfileId, patientId })

      cachedPatientData[cachedKey] = {
        ...cachedPatientData[cachedKey],
        ...ajaxPatientData,
        isLoaded: true,
      }

      patientData.value = cachedPatientData[currentCachedKey] || { ...EMPTY_PATIENT_DATA }
    } catch {
      cachedPatientData[cachedKey] = null
    }
  }

  return {
    isVisible,
    patientData,
    open,
    close,
  }
}
